/**
 * Pure raise-package field map: money, words, filenames, factual overview.
 * Port of generate_raise_package.py compute_fields / package_filenames without
 * the unevidenced genealogist / attorney-filing sentence.
 */

export const RAISE_PACKAGE_PLACEHOLDERS = [
  "investor_name",
  "principal",
  "principal_plain",
  "principal_words",
  "interest_rate",
  "interest_rate_int",
  "interest_rate_words",
  "interest_amount",
  "interest_amount_plain",
  "maturity_payment",
  "maturity_payment_plain",
  "maturity_payment_phrase",
  "term_months",
  "property_address",
  "property_street",
  "deal_short",
  "county",
  "state",
  "state_name",
  "estimated_excess_proceeds",
  "heir_count",
  "heir_count_words",
  "ownership_interest",
  "investment_overview_paragraph",
  "borrower_name",
  "legal_borrower",
  "governing_law_state"
] as const;

export type RaisePackagePlaceholder = (typeof RAISE_PACKAGE_PLACEHOLDERS)[number];
export type RaisePackageFields = Record<RaisePackagePlaceholder, string>;

export type RaisePackageInput = {
  investor_name?: string;
  principal?: number | string;
  interest_rate?: number | string;
  term_months?: number | string;
  property_address?: string;
  property_street?: string;
  deal_short?: string;
  county?: string;
  state?: string;
  state_name?: string;
  estimated_excess_proceeds?: number | string;
  heir_count?: number | string;
  ownership_interest?: string;
  investment_overview_paragraph?: string;
  borrower_name?: string;
  legal_borrower?: string;
  governing_law_state?: string;
};

const ONES = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen"
];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

const STATE_NAMES: Record<string, string> = {
  NC: "North Carolina",
  SC: "South Carolina",
  VA: "Virginia",
  GA: "Georgia",
  TN: "Tennessee",
  FL: "Florida",
  AL: "Alabama",
  KY: "Kentucky",
  WV: "West Virginia",
  MD: "Maryland",
  TX: "Texas",
  CA: "California",
  NY: "New York"
};

const STATE_CODES: Record<string, string> = {
  "north carolina": "NC",
  "south carolina": "SC",
  virginia: "VA",
  georgia: "GA",
  tennessee: "TN",
  florida: "FL",
  alabama: "AL",
  kentucky: "KY",
  "west virginia": "WV",
  maryland: "MD",
  texas: "TX",
  california: "CA",
  "new york": "NY"
};

const DEFAULT_BORROWER = "Sovereign Overflow LLC";
export const DEFAULT_INTEREST_RATE = 20;
export const DEFAULT_TERM_MONTHS = 12;

function chunkWords(n: number): string {
  if (n < 20) return ONES[n] ?? String(n);
  if (n < 100) {
    const t = Math.trunc(n / 10);
    const o = n % 10;
    return TENS[t] + (o ? `-${ONES[o]}` : "");
  }
  const h = Math.trunc(n / 100);
  const rest = n % 100;
  if (rest === 0) return `${ONES[h]} hundred`;
  return `${ONES[h]} hundred ${chunkWords(rest)}`;
}

export function numberToWords(n: number): string {
  const value = Math.trunc(n);
  if (value < 0) return `negative ${numberToWords(-value)}`;
  if (value < 1000) return chunkWords(value);
  const parts: string[] = [];
  let remaining = value;
  for (const [scale, name] of [
    [1_000_000_000, "billion"],
    [1_000_000, "million"],
    [1_000, "thousand"]
  ] as const) {
    if (remaining >= scale) {
      const qty = Math.trunc(remaining / scale);
      remaining %= scale;
      parts.push(`${chunkWords(qty)} ${name}`);
    }
  }
  if (remaining) parts.push(chunkWords(remaining));
  return parts.join(" ");
}

export function wordsTitle(n: number): string {
  return numberToWords(n).replace(/(^|[\s-])(\w)/g, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
}

function stripMoney(raw: string): string {
  return raw.trim().replaceAll("$", "").replaceAll(",", "");
}

/** Parse a decimal amount to integer cents using half-up on the third fractional digit. */
export function parseToCents(value: number | string): number {
  const raw = typeof value === "number" ? String(value) : stripMoney(String(value));
  if (!raw || raw === "." || raw === "-" || raw === "-.") return Number.NaN;
  const negative = raw.startsWith("-");
  const unsigned = negative ? raw.slice(1) : raw;
  const [wholePart = "0", fracPart = ""] = unsigned.split(".");
  if (!/^\d*$/.test(wholePart) || !/^\d*$/.test(fracPart)) return Number.NaN;
  const whole = Number(wholePart || "0");
  if (!Number.isFinite(whole)) return Number.NaN;
  const digits = `${fracPart}000`.slice(0, 3);
  const cents = whole * 100 + Number(digits.slice(0, 2));
  const tenths = Number(digits[2] ?? "0");
  const rounded = tenths >= 5 ? cents + 1 : cents;
  return negative ? -rounded : rounded;
}

function mulDivHalfUp(amount: number, numerator: number, denominator: number): number {
  const product = amount * numerator;
  const quotient = Math.trunc(product / denominator);
  const remainder = product % denominator;
  if (remainder * 2 >= denominator) return quotient + 1;
  if (remainder * 2 <= -denominator) return quotient - 1;
  return quotient;
}

function formatMoney(cents: number, keepZeroCents: boolean): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const frac = abs % 100;
  const sign = negative ? "-" : "";
  if (!keepZeroCents && frac === 0) return `${sign}$${whole.toLocaleString("en-US")}`;
  return `${sign}$${whole.toLocaleString("en-US")}.${String(frac).padStart(2, "0")}`;
}

export function money(cents: number): string {
  return formatMoney(cents, true);
}

export function moneyPlain(cents: number): string {
  return formatMoney(cents, false);
}

export function isWholeDollarPrincipal(value: number | string): boolean {
  const cents = parseToCents(value);
  return Number.isInteger(cents) && cents > 0 && cents % 100 === 0;
}

export function parseRateHundredths(value: number | string | undefined): number {
  const raw = value === undefined || value === "" ? String(DEFAULT_INTEREST_RATE) : String(value).trim().replace("%", "");
  const cents = parseToCents(raw);
  return Number.isFinite(cents) ? cents : parseToCents(DEFAULT_INTEREST_RATE);
}

export function stateName(codeOrName: string): string {
  const s = (codeOrName || "").trim();
  if (!s) return "North Carolina";
  if (s.toUpperCase() in STATE_NAMES) return STATE_NAMES[s.toUpperCase()] ?? s;
  return s;
}

export function stateCode(codeOrName: string): string {
  const s = (codeOrName || "").trim();
  if (!s) return "NC";
  if (s.length === 2) return s.toUpperCase();
  return STATE_CODES[s.toLowerCase()] ?? (s.length >= 2 ? s.toUpperCase().slice(0, 2) : "NC");
}

export function dealShortFromStreet(street: string): string {
  return (street || "")
    .trim()
    .replace(/\bStreet\b/gi, "ST")
    .replace(/\bAvenue\b/gi, "AVE")
    .replace(/\bRoad\b/gi, "RD")
    .replace(/\bDrive\b/gi, "DR")
    .replace(/\bLane\b/gi, "LN")
    .replace(/\bBoulevard\b/gi, "BLVD")
    .replace(/\bCourt\b/gi, "CT");
}

export function slug(value: string): string {
  const cleaned = (value || "").replace(/[^\w\s-]/g, "").replace(/\s+/g, " ").trim();
  return cleaned || "Deal";
}

export function composeFactualOverview(input: {
  property_address?: string;
  county?: string;
  state_name?: string;
  heir_count?: string;
  heir_count_words?: string;
  ownership_interest?: string;
}): string {
  const address = (input.property_address || "").trim();
  const county = (input.county || "").trim();
  if (!address || !county) return "";
  const stName = (input.state_name || "North Carolina").trim();
  let text = `This investment is tied to ${address} in ${county} County, ${stName}.`;
  const heirsWords = (input.heir_count_words || "").trim();
  const heirs = (input.heir_count || "").trim();
  if (heirsWords && /^\d+$/.test(heirs)) {
    const titled = heirsWords.charAt(0).toUpperCase() + heirsWords.slice(1);
    text += ` ${titled} heirs inherited the property.`;
  } else if (heirs) {
    text += ` ${heirs} heirs inherited the property.`;
  }
  const own = (input.ownership_interest || "").trim();
  if (own) {
    text += ` InheritedProperty.com acquired one heir's ${own} ownership interest before foreclosure.`;
  }
  return text;
}

export function computeRaisePackageFields(raw: RaisePackageInput): RaisePackageFields {
  const principalCents = parseToCents(raw.principal ?? 0);
  const rateHundredths = parseRateHundredths(raw.interest_rate);
  const term = Number.parseInt(String(raw.term_months ?? DEFAULT_TERM_MONTHS), 10) || DEFAULT_TERM_MONTHS;
  const interestCents = mulDivHalfUp(principalCents, rateHundredths * term, 10_000 * 12);
  const maturityCents = principalCents + interestCents;
  const rateInt = Math.trunc(rateHundredths / 100);

  const street = String(raw.property_street || raw.property_address || "").trim();
  const address = String(raw.property_address || street).trim();
  const county = String(raw.county || "").trim();
  const st = raw.state || "NC";
  const stName = stateName(String(raw.state_name || st));
  const stCode = stateCode(String(st));
  const heirsRaw = String(raw.heir_count ?? "").trim();
  const heirsN = heirsRaw && /^-?\d+(\.0+)?$/.test(heirsRaw) ? Number.parseInt(heirsRaw, 10) : Number.NaN;
  let own = String(raw.ownership_interest || "").trim();
  if (own && !own.endsWith("%") && /^[\d.]+$/.test(own)) own = `${own}%`;

  const excessRaw = String(raw.estimated_excess_proceeds ?? "").trim();
  const excessCents = excessRaw ? parseToCents(excessRaw) : Number.NaN;
  const excess = Number.isFinite(excessCents) ? moneyPlain(excessCents) : "";

  const deal = String(raw.deal_short || dealShortFromStreet(street)).trim();
  const investor = String(raw.investor_name || "").trim();
  const borrower = String(raw.borrower_name || raw.legal_borrower || DEFAULT_BORROWER).trim();
  const gov = String(raw.governing_law_state || stName).trim();
  const heirCount = Number.isFinite(heirsN) ? String(heirsN) : heirsRaw;
  const heirWords = Number.isFinite(heirsN) ? numberToWords(heirsN) : heirsRaw;

  const override = String(raw.investment_overview_paragraph || "").trim();
  const overview =
    override ||
    composeFactualOverview({
      property_address: address,
      county,
      state_name: stName,
      heir_count: heirCount,
      heir_count_words: heirWords,
      ownership_interest: own
    });

  const fields: RaisePackageFields = {
    investor_name: investor,
    principal: Number.isFinite(principalCents) ? money(principalCents) : "",
    principal_plain: Number.isFinite(principalCents) ? moneyPlain(principalCents) : "",
    principal_words: Number.isFinite(principalCents) ? wordsTitle(Math.trunc(principalCents / 100)) : "",
    interest_rate: `${(rateHundredths / 100).toFixed(2)}%`,
    interest_rate_int: `${rateInt}%`,
    interest_rate_words: numberToWords(rateInt),
    interest_amount: Number.isFinite(interestCents) ? money(interestCents) : "",
    interest_amount_plain: Number.isFinite(interestCents) ? moneyPlain(interestCents) : "",
    maturity_payment: Number.isFinite(maturityCents) ? money(maturityCents) : "",
    maturity_payment_plain: Number.isFinite(maturityCents) ? moneyPlain(maturityCents) : "",
    maturity_payment_phrase: Number.isFinite(maturityCents)
      ? `${moneyPlain(maturityCents)} at ${term}-month term`
      : "",
    term_months: String(term),
    property_address: address,
    property_street: street,
    deal_short: deal,
    county,
    state: stCode,
    state_name: stName,
    estimated_excess_proceeds: excess,
    heir_count: heirCount,
    heir_count_words: heirWords,
    ownership_interest: own,
    investment_overview_paragraph: overview,
    borrower_name: borrower,
    legal_borrower: borrower,
    governing_law_state: gov
  };
  return fields;
}

export function raisePackageFilenames(fields: RaisePackageFields): {
  memorandum: string;
  note: string;
  wire: string;
} {
  const investor = slug(fields.investor_name);
  const deal = slug(fields.deal_short || fields.property_street || fields.property_address);
  return {
    memorandum: `${investor} - ${deal} - Investment Memorandum.docx`,
    note: `${investor} - ${deal} - Promissory Note.docx`,
    wire: `${investor} - ${deal} - Wire Transfer Instructions.docx`
  };
}

export function emptyRaisePackageFields(): RaisePackageFields {
  return Object.fromEntries(RAISE_PACKAGE_PLACEHOLDERS.map((key) => [key, ""])) as RaisePackageFields;
}
